import React, { useState } from "react";
import { MainPolicySettingBlock, PolicyCustomizeSection, SubPolicySettingBlock } from "uis/Section";
import UI from "@mile-meeting/mile-ui";
import { useToast } from "hook/useToast";
import AlertModal from "uis/modal/alert-modal";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { getToggleState } from "utils/meetingSettingUtils.service";
import { MeetingRoomSetting } from "./MeetingRoomPolicyModal/types";
import { getTabletMeetingFinishAlertDetail, updateTabletMeetingFinishAlertDetail } from "apis/meeting-settings/tablet";
import MeetingRoomPolicyModal from "./MeetingRoomPolicyModal/MeetingRoomPolicyModal";
import ToolTip from "uis/new-tootip";

type PreMeetingEndAlertSettingProps = {
  hasMeetingRoomUsedTablet: boolean;
  meetingFinishAlertActive: GLOBAL_MEETING_SETTING_VALUE;
  meetingFinishAlertTime: number;
  meetingFinishSoundAlertActive: GLOBAL_MEETING_SETTING_VALUE;
  onChangeMeetingFinishAlert: (active: GLOBAL_MEETING_SETTING_VALUE) => void;
  onChangeMeetingFinishAlertTime: (time: number) => void;
  onChangeMeetingFinishSoundAlert: (active: GLOBAL_MEETING_SETTING_VALUE) => void;
  onUpdate: () => void;
};
const PreMeetingEndAlertSetting: React.FC<PreMeetingEndAlertSettingProps> = ({
  hasMeetingRoomUsedTablet,
  meetingFinishAlertActive,
  meetingFinishAlertTime,
  meetingFinishSoundAlertActive,
  onChangeMeetingFinishAlert,
  onChangeMeetingFinishAlertTime,
  onChangeMeetingFinishSoundAlert,
  onUpdate,
}) => {
  const { showToast } = useToast();
  const [openMeetingFinishAlertTimeBulkModal, setOpenMeetingFinishAlertTimeBulkModal] = useState<boolean>(false);
  const [openMeetingFinishAlertSoundBulkModal, setOpenMeetingFinishAlertSoundBulkModal] = useState<boolean>(false);
  const [selectedMeetingFinishAlertTime, setSelectedMeetingFinishAlertTime] = useState<number>(-1);
  const [meetingFinishAlertActiveModal, setMeetingFinishAlertActiveModal] = useState<boolean>(false);
  const [meetingFinishAlertUnActiveModal, setMeetingFinishAlertUnActiveModal] = useState<boolean>(false);
  const [meetingFinishAlertDetailSetting, setMeetingFinishAlertDetailSetting] = useState<MeetingRoomSetting[]>([]);
  const [meetingFinishAlertDetailSettingModal, setMeetingFinishAlertDetailSettingModal] = useState<boolean>(false);

  const getMeetingFinishAlertDetailSetting = async () => {
    const res = await getTabletMeetingFinishAlertDetail();
    if (res.success && res.data) {
      setMeetingFinishAlertDetailSetting(res.data);
    }
  };

  const onUpdateMeetingFinishAlertDetailSetting = async (meetingRoomSetting: MeetingRoomSetting[]) => {
    const res = await updateTabletMeetingFinishAlertDetail(meetingRoomSetting);
    if (res.success) {
      showToast("변경사항이 성공적으로 저장되었습니다.", "success");
      onUpdate();
      setMeetingFinishAlertDetailSettingModal(false);
    } else {
      showToast("변경사항 저장에 실패했습니다.", "error");
    }
  };

  return (
    <PolicyCustomizeSection
      title="회의 종료 안내 설정"
      rightChildren={
        <UI.ButtonGroups.Text
          title="회의 종료 안내 기능 더 알아보기"
          size="small"
          onClick={() =>
            window.open(
              "https://mile-corporation.notion.site/12ea222a9c1b819b9c04cac8e4f1d746#232a222a9c1b8018a19efb9673736b8a"
            )
          }
        />
      }
    >
      <MainPolicySettingBlock
        title="회의 종료 안내 기능 활성화"
        description="활성화 시 회의 종료 전 태블릿에서 안내 팝업을 표시합니다."
        haveChildren={meetingFinishAlertActive !== GLOBAL_MEETING_SETTING_VALUE.OFF}
        showAdvancedSettings
        disabledAdvancedSettings={meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF}
        onClickAdvancedSettings={() => {
          if (meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
            showToast("회의 종료 안내 기능을 활성화해야 상세 설정이 가능합니다.", "warning");
            return;
          }

          getMeetingFinishAlertDetailSetting();
          setMeetingFinishAlertDetailSettingModal(true);
        }}
        rightChildren={
          <ToolTip
            text={
              meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON
                ? "전체 회의실 일괄 설정된 상태입니다."
                : meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                  ? "회의실별 상세 설정된 상태입니다."
                  : undefined
            }
            position={{
              top: -30,
              left: meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
            }}
            icon={
              <div style={{ position: "relative" }}>
                <UI.Selection.Toggle.TriState
                  size="large"
                  state={getToggleState(meetingFinishAlertActive)}
                  onChange={() =>
                    meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                      ? setMeetingFinishAlertActiveModal(true)
                      : setMeetingFinishAlertUnActiveModal(true)
                  }
                  disabled={!hasMeetingRoomUsedTablet}
                />
                {!hasMeetingRoomUsedTablet && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={() =>
                      showToast(
                        "회의 종료 안내 기능은 최소 1개 이상의 회의실이 내부 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                        "warning"
                      )
                    }
                  />
                )}
              </div>
            }
          />
        }
      />
      {meetingFinishAlertActive !== GLOBAL_MEETING_SETTING_VALUE.OFF && (
        <SubPolicySettingBlock
          title="안내 알림 시간"
          description="회의 종료 기준으로 설정된 시간 이전 시점에 태블릿에서 안내 팝업이 표시됩니다."
          rightChildren={
            <div style={{ display: "flex", alignItems: "flex-end", flexDirection: "column", gap: "8px", zIndex: 51 }}>
              <UI.SelectBox
                size="small"
                data={["5분", "10분", "15분"]}
                selectedData={[meetingFinishAlertTime === -1 ? "-" : meetingFinishAlertTime + "분"]}
                width={120}
                dropdownMaxHeight={120}
                onChange={(time: string[]) => {
                  if (meetingFinishAlertTime === -1) {
                    setOpenMeetingFinishAlertTimeBulkModal(true);
                    setSelectedMeetingFinishAlertTime(Number(time[0].replaceAll("분", "")));
                    return;
                  }
                  onChangeMeetingFinishAlertTime(Number(time[0].replaceAll("분", "")));
                }}
                disabled={!hasMeetingRoomUsedTablet}
              />
              {meetingFinishAlertTime === -1 && (
                <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                  회의실별 상세 설정된 상태입니다.
                </UI.Typography>
              )}
            </div>
          }
        />
      )}
      {meetingFinishAlertActive !== GLOBAL_MEETING_SETTING_VALUE.OFF && (
        <SubPolicySettingBlock
          title="회의 종료 안내 소리 알림 활성화"
          description="설정한 시간에 회의 종료 안내 팝업과 함께 소리가 울립니다."
          rightChildren={
            <ToolTip
              text={
                meetingFinishSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON
                  ? "전체 회의실 일괄 설정된 상태입니다."
                  : meetingFinishSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                    ? "회의실별 상세 설정된 상태입니다."
                    : undefined
              }
              position={{
                top: -30,
                left: meetingFinishSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
              }}
              icon={
                <div style={{ position: "relative" }}>
                  <UI.Selection.Toggle.TriState
                    size="large"
                    state={getToggleState(meetingFinishSoundAlertActive)}
                    onChange={() => {
                      if (meetingFinishSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL) {
                        setOpenMeetingFinishAlertSoundBulkModal(true);
                        return;
                      }
                      onChangeMeetingFinishSoundAlert(
                        meetingFinishSoundAlertActive === GLOBAL_MEETING_SETTING_VALUE.OFF
                          ? GLOBAL_MEETING_SETTING_VALUE.ON
                          : GLOBAL_MEETING_SETTING_VALUE.OFF
                      );
                    }}
                    disabled={!hasMeetingRoomUsedTablet}
                  />
                  {!hasMeetingRoomUsedTablet && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      onClick={() =>
                        showToast(
                          "태블릿 정책은 최소 1개 이상의 회의실이 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                          "warning"
                        )
                      }
                    />
                  )}
                </div>
              }
            />
          }
        />
      )}
      <MeetingRoomPolicyModal
        modalIsOpen={meetingFinishAlertDetailSettingModal}
        closeModal={() => setMeetingFinishAlertDetailSettingModal(false)}
        title="회의 종료 안내 기능 회의실별 상세 설정"
        settingData={meetingFinishAlertDetailSetting}
        onUpdate={onUpdateMeetingFinishAlertDetailSetting}
        metaData={[
          {
            key: "isFinishNotiEnabled",
            label: `회의 종료 안내\n기능 활성화`,
            isMainPolicy: true,
            type: "toggle",
          },
          {
            key: "finishNotiTime",
            label: `안내 알림 시간`,
            type: "select",
            options: [5, 10, 15],
            disabledWhen: (data: MeetingRoomSetting) => {
              return !data["isFinishNotiEnabled"];
            },
          },
          {
            key: "isFinishNotiSoundAlertEnabled",
            label: `회의 종료 안내 소리\n알림 활성화`,
            type: "toggle",
            disabledWhen: (data: MeetingRoomSetting) => {
              return !data["isFinishNotiEnabled"];
            },
          },
        ]}
      />
      <AlertModal
        title={
          <p>
            회의 종료 안내 기능을
            <br />
            활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            회의 종료 기준으로 설정된 시간 이전 시점에
            <br />
            태블릿에서 안내 팝업이 표시됩니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeMeetingFinishAlert(GLOBAL_MEETING_SETTING_VALUE.ON);
          setMeetingFinishAlertActiveModal(false);
        }}
        modalIsOpen={meetingFinishAlertActiveModal}
        closeModal={() => setMeetingFinishAlertActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            회의 종료 안내 기능을
            <br />
            {meetingFinishAlertActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL ? "일괄" : ""} 비활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            태블릿에서 회의 종료에 대한 안내 팝업이
            <br />
            표시되지 않습니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          onChangeMeetingFinishAlert(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setMeetingFinishAlertUnActiveModal(false);
        }}
        modalIsOpen={meetingFinishAlertUnActiveModal}
        closeModal={() => setMeetingFinishAlertUnActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            안내 알림 시간을
            <br />
            일괄 변경하시겠습니까?
          </p>
        }
        content="회의실별 상세 설정된 값이 일괄 변경됩니다."
        btnTitle="변경하기"
        onChange={() => {
          onChangeMeetingFinishAlertTime(selectedMeetingFinishAlertTime);
          setOpenMeetingFinishAlertTimeBulkModal(false);
        }}
        modalIsOpen={openMeetingFinishAlertTimeBulkModal}
        closeModal={() => setOpenMeetingFinishAlertTimeBulkModal(false)}
      />
      <AlertModal
        title={
          <p>
            회의 종료 안내 소리 알림을
            <br />
            일괄 비활성화하시겠습니까?
          </p>
        }
        btnTitle="변경하기"
        content="회의실별 상세 설정된 값이 일괄 변경됩니다."
        onChange={() => {
          onChangeMeetingFinishSoundAlert(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setOpenMeetingFinishAlertSoundBulkModal(false);
        }}
        modalIsOpen={openMeetingFinishAlertSoundBulkModal}
        closeModal={() => setOpenMeetingFinishAlertSoundBulkModal(false)}
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(PreMeetingEndAlertSetting);
